import React, { useState } from 'react';
import { relicById } from '../game/relics';
import { BLESSINGS } from '../game/blessings';

// v3.1 QoL: Rich tooltips for relics and blessings — show pickup floor + full description.
// Two variants exported. Both use the same "hover a chip → floating popup" pattern.

export function RelicBadge({ relicId, pickupFloor, triggers, size = 'md', testid }) {
  const [open, setOpen] = useState(false);
  const r = relicById(relicId);
  if (!r) return null;
  const cls = size === 'sm' ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-1';
  return (
    <span
      className={`brutal-border-pink ${cls} uppercase font-bold text-[color:var(--pink)] inline-flex items-center gap-1 cursor-help relative`}
      data-testid={testid || `relic-badge-${relicId}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <span aria-hidden="true">{r.art}</span> {r.name}
      {open && (
        <RelicTooltip relic={r} pickupFloor={pickupFloor} triggers={triggers} />
      )}
    </span>
  );
}

function RelicTooltip({ relic, pickupFloor, triggers }) {
  return (
    <div
      className="brutal-border-dim bg-[#0a0a0a] p-3 text-left normal-case font-mono tracking-normal"
      data-testid={`relic-tooltip-${relic.id}`}
      style={{
        position: 'absolute',
        bottom: '110%',
        left: '50%',
        transform: 'translateX(-50%)',
        minWidth: 240,
        maxWidth: 320,
        zIndex: 40,
        borderColor: 'var(--pink)',
        boxShadow: '4px 4px 0 var(--pink)',
      }}
    >
      <div className="text-[color:var(--pink)] font-display font-black uppercase tracking-widest text-sm mb-1">
        {relic.art} {relic.name}
      </div>
      <div className="text-xs text-white leading-relaxed">
        {relic.desc}
      </div>
      <div className="mt-2 flex items-center gap-3 text-[10px] uppercase tracking-widest text-[color:var(--text-dim)] border-t border-[color:var(--muted)] pt-2">
        {pickupFloor != null && pickupFloor > 0 && (
          <span data-testid={`relic-pickup-${relic.id}`}>PICKED UP · FLOOR {pickupFloor}</span>
        )}
        {typeof triggers === 'number' && triggers > 0 && (
          <span className="text-[color:var(--neon)]" data-testid={`relic-triggers-${relic.id}`}>× {triggers} TRIGGERS</span>
        )}
        {(pickupFloor == null || pickupFloor === 0) && !(typeof triggers === 'number' && triggers > 0) && (
          <span>STARTER RELIC</span>
        )}
      </div>
    </div>
  );
}

export function BlessingBadge({ blessingId, pickupFloor, testid }) {
  const [open, setOpen] = useState(false);
  const b = BLESSINGS[blessingId];
  if (!b) return null;
  const rarity = b.rarity || 'common';
  const rarityColor = {
    common: '#8a8a8a', uncommon: '#00e5ff', rare: '#ffd700', legendary: '#ff2ea6',
  }[rarity] || '#8a8a8a';
  return (
    <span
      className="brutal-border-dim text-xs px-2 py-1 uppercase font-bold inline-flex items-center gap-1 cursor-help relative"
      style={{ color: rarityColor, borderColor: rarityColor }}
      data-testid={testid || `blessing-badge-${blessingId}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <span aria-hidden="true">✧</span> {b.name}
      {open && (
        <div
          className="brutal-border-dim bg-[#0a0a0a] p-3 text-left normal-case font-mono tracking-normal"
          data-testid={`blessing-tooltip-${blessingId}`}
          style={{
            position: 'absolute',
            bottom: '110%',
            left: '50%',
            transform: 'translateX(-50%)',
            minWidth: 240,
            maxWidth: 320,
            zIndex: 40,
            borderColor: rarityColor,
            boxShadow: `4px 4px 0 ${rarityColor}`,
          }}
        >
          <div className="font-display font-black uppercase tracking-widest text-sm mb-1" style={{ color: rarityColor }}>
            ✧ {b.name}
            <span className="ml-2 text-[9px] opacity-70">· {rarity}</span>
          </div>
          <div className="text-xs text-white leading-relaxed">{b.desc}</div>
          <div className="mt-2 text-[10px] uppercase tracking-widest text-[color:var(--text-dim)] border-t border-[color:var(--muted)] pt-2">
            {pickupFloor != null && pickupFloor > 0 ? (
              <span data-testid={`blessing-pickup-${blessingId}`}>OBTAINED · FLOOR {pickupFloor}</span>
            ) : (
              <span>BOSS BLESSING</span>
            )}
          </div>
        </div>
      )}
    </span>
  );
}
